import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress
} from '@mui/material';
import { Storage, QueryStats, CloudQueue, CloudUpload, Timeline, FolderShared, Source, Settings, BubbleChart } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useEntityContext } from '../EntityContextSwitch';
import DataSummary from './DataSummary';
import DataSources from './DataSources';
import IngestionPoints from '../Admin/IngestionPoints';
import ProcessVisibilityAPI from './ProcessVisibilityAPI'; // API version
import SharedDrives from '../DocumentManagement/SharedDrives';
import TokenExaminerTab from './TokenExaminerTab';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`data-management-tabpanel-${index}`}
      aria-labelledby={`data-management-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// eslint-disable-next-line max-lines-per-function
function DataManagement() {
  const [parentTab, setParentTab] = useState(0); // 0 = Sources, 1 = Processing
  const [sourcesSubTab, setSourcesSubTab] = useState(0);
  const [processingSubTab, setProcessingSubTab] = useState(0);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const { currentEntity, isInitializing } = useEntityContext();

  const clientId = currentEntity?.id;

  // Validate client context
  useEffect(() => {
    if (!isInitializing && !clientId) {
      setError('No entity selected. Please select an entity from the dashboard.');
    } else if (clientId) {
      setError(null); // Clear error when entity is selected
    }
  }, [clientId, isInitializing]);

  const handleParentTabChange = (event, newValue) => {
    setParentTab(newValue);
  };

  const handleSourcesSubTabChange = (event, newValue) => {
    setSourcesSubTab(newValue);
  };

  const handleProcessingSubTabChange = (event, newValue) => {
    setProcessingSubTab(newValue);
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Please sign in to access data management features.
        </Alert>
      </Box>
    );
  }

  if (isInitializing) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!clientId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            Entity Selection Required
          </Typography>
          <Typography variant="body2">
            Please select an entity from the dropdown in the header before accessing data management features.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Storage color="primary" />
        Data
      </Typography>

      {/* Parent Tabs */}
      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={parentTab}
          onChange={handleParentTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label="Sources"
            icon={<Source />}
            iconPosition="start"
            sx={{ minHeight: 64 }}
          />
          <Tab
            label="Processing"
            icon={<Settings />}
            iconPosition="start"
            sx={{ minHeight: 64 }}
          />
        </Tabs>

        {/* Sources Tab Content */}
        {parentTab === 0 && (
          <Box>
            <Tabs
              value={sourcesSubTab}
              onChange={handleSourcesSubTabChange}
              indicatorColor="secondary"
              textColor="secondary"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: 'action.hover'
              }}
            >
              <Tab
                label="Summary"
                icon={<QueryStats />}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
              <Tab
                label="Data Sources"
                icon={<CloudQueue />}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
              <Tab
                label="Ingestion Points"
                icon={<CloudUpload />}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
              <Tab
                label="Shared Drives"
                icon={<FolderShared />}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
            </Tabs>

            <Box sx={{ p: 3 }}>
              <TabPanel value={sourcesSubTab} index={0}>
                <DataSummary />
              </TabPanel>

              <TabPanel value={sourcesSubTab} index={1}>
                <DataSources />
              </TabPanel>

              <TabPanel value={sourcesSubTab} index={2}>
                <IngestionPoints />
              </TabPanel>

              <TabPanel value={sourcesSubTab} index={3}>
                <SharedDrives />
              </TabPanel>
            </Box>
          </Box>
        )}

        {/* Processing Tab Content */}
        {parentTab === 1 && (
          <Box>
            <Tabs
              value={processingSubTab}
              onChange={handleProcessingSubTabChange}
              indicatorColor="secondary"
              textColor="secondary"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: 'action.hover'
              }}
            >
              <Tab
                label="Process Visibility"
                icon={<Timeline />}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
              <Tab
                label="Token Examiner"
                icon={<BubbleChart />}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
            </Tabs>

            <Box sx={{ p: 3 }}>
              <TabPanel value={processingSubTab} index={0}>
                <ProcessVisibilityAPI entityId={clientId} />
              </TabPanel>

              <TabPanel value={processingSubTab} index={1}>
                <TokenExaminerTab entityId={clientId} />
              </TabPanel>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default DataManagement;